import type { SSE } from 'sse.js'
import { buildSSEClient } from '~/api/utils'

const SUBJECT_MARKER = '---SUBJECT---'
const CONTENT_MARKER = '---CONTENT---'

function extractStreamingContent(accumulated: string): string | null {
  const idx = accumulated.indexOf(CONTENT_MARKER)
  if (idx === -1) return null
  return accumulated.slice(idx + CONTENT_MARKER.length).replace(/^\r?\n/, '')
}

function parseStreamedDraft(raw: string): { subject: string; content: string } {
  const subjectIdx = raw.indexOf(SUBJECT_MARKER)
  const contentIdx = raw.indexOf(CONTENT_MARKER)
  let subject = ''
  let content = ''
  if (subjectIdx !== -1 && contentIdx !== -1) {
    subject = raw.slice(subjectIdx + SUBJECT_MARKER.length, contentIdx).trim()
    content = raw
      .slice(contentIdx + CONTENT_MARKER.length)
      .replace(/^\r?\n/, '')
      .trim()
  } else if (contentIdx !== -1) {
    content = raw
      .slice(contentIdx + CONTENT_MARKER.length)
      .replace(/^\r?\n/, '')
      .trim()
  } else {
    content = raw.trim()
  }
  return { subject, content }
}

type DraftCallbacks = {
  onContent: (partial: string) => void
  onDone: (subject: string, content: string) => void
  onError: () => void
}

export function startContactEmailDraft(
  applicantId: number,
  orderId: number,
  callbacks: DraftCallbacks
): SSE {
  let accumulated = ''

  const source = buildSSEClient('v1/ai/contact-email-draft', {
    applicantId,
    orderId
  })

  source.addEventListener('done', (e: { data: string }) => {
    const { subject, content } = parseStreamedDraft(accumulated)
    const { signature } = JSON.parse(e.data) as { signature: string }
    callbacks.onDone(subject, signature ? content + signature : content)
  })

  source.addEventListener('message', (e: { data: string }) => {
    try {
      const chunk = JSON.parse(e.data)
      const delta: string = chunk?.delta?.content ?? ''
      if (delta) {
        accumulated += delta
        const partial = extractStreamingContent(accumulated)
        if (partial !== null) {
          callbacks.onContent(partial)
        }
      }
    } catch {
      source.close()
      callbacks.onError()
    }
  })

  source.addEventListener('error', () => {
    source.close()
    callbacks.onError()
  })

  source.stream()
  return source
}
